import { getAssistantSurface } from './assistant-actions'
import type { ExecuteClientToolDeps } from './client-tool-types'

/** The pages `show_security` opens, as the model names them. */
const VIEWS = ['overview', 'scan_type', 'target', 'agents'] as const
type SecurityView = (typeof VIEWS)[number]

const SCAN_TYPES = ['arcgis', 'database', 'external', 'internal', 'host'] as const

/** Matches the server's cap on a target id in the page context. */
const MAX_ID = 64

/** A page reads its target before it reports it; past this the page is shown without a claim. */
const SHOW_WAIT_MS = 4_000
const POLL_MS = 100

const delay = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms))

interface ShowRequest {
  view: SecurityView
  scanType?: string
  targetId?: string
}

function readRequest(input: unknown): ShowRequest | { error: string } {
  const raw = (input ?? {}) as { view?: unknown; scan_type?: unknown; target_id?: unknown }
  const rawView = raw.view === undefined ? 'overview' : raw.view

  if (!VIEWS.includes(rawView as SecurityView)) {
    return { error: "Invalid view: expected 'overview', 'scan_type', 'target' or 'agents'." }
  }

  const view = rawView as SecurityView

  if (raw.scan_type !== undefined && !SCAN_TYPES.includes(raw.scan_type as (typeof SCAN_TYPES)[number])) {
    return { error: 'Invalid scan_type.' }
  }

  const scanType = raw.scan_type as string | undefined

  let targetId: string | undefined

  if (raw.target_id !== undefined) {
    if (typeof raw.target_id !== 'string' || raw.target_id.trim().length > MAX_ID) {
      return { error: 'Invalid target_id.' }
    }

    targetId = raw.target_id.trim()
  }

  if (view === 'scan_type') {
    if (!scanType) return { error: "The 'scan_type' view needs a scan_type." }

    return { view, scanType }
  }

  if (view === 'target') {
    if (!targetId) return { error: "The 'target' view needs a target_id." }

    return { view, targetId }
  }

  return { view }
}

function pathOf(req: ShowRequest): string {
  if (req.view === 'scan_type') return `/security/${req.scanType}`
  if (req.view === 'target') return `/security/scan-targets/${encodeURIComponent(req.targetId!)}`
  if (req.view === 'agents') return '/security/agents'

  return '/security'
}

/** True once the Security page on screen reports the requested view. */
function showing(req: ShowRequest): boolean {
  const handle = getAssistantSurface()
  if (handle?.kind !== 'security') return false

  const ctx = handle.readContext().security
  if (ctx.view !== req.view) return false
  // A page that is still (re)reading reports the previous page's numbers.
  if (ctx.loading) return false
  if (req.view === 'scan_type') return ctx.scanType === req.scanType
  if (req.view === 'target') return ctx.target?.id === req.targetId

  return true
}

async function awaitShowing(req: ShowRequest): Promise<boolean> {
  const deadline = Date.now() + SHOW_WAIT_MS

  for (;;) {
    if (showing(req)) return true
    if (Date.now() >= deadline) return false
    await delay(POLL_MS)
  }
}

/**
 * `show_security`. `refusal` is the route manifest's answer for `/security` for this caller,
 * resolved by `executeClientTool` (which owns the resolver), or null when allowed.
 */
export async function executeShowSecurity(
  input: unknown,
  deps: ExecuteClientToolDeps,
  refusal: string | null,
): Promise<unknown> {
  const req = readRequest(input)
  if ('error' in req) return req

  if (refusal) return { refused: refusal }

  const target = pathOf(req)
  const onTargetPage = deps.currentRoute() === target
  const discard = (input as { discardUnsaved?: unknown } | null)?.discardUnsaved === true

  if (!onTargetPage && !discard) {
    const unsaved = deps.hasUnsavedWork()
    if (unsaved) return { blocked_unsaved_work: unsaved }
  }

  // Showing what is already shown must not add a Back entry.
  const currentQuery = (deps.currentSearch?.() ?? '').replace(/^\?/, '')
  if (onTargetPage && currentQuery === '') deps.navigate(target, { replace: true })
  else deps.navigate(target)

  const targetId = req.targetId ? { target_id: req.targetId } : {}

  return (await awaitShowing(req))
    ? { shown: true, view: req.view, ...targetId }
    : { shown: true, note: 'The Security page did not report this view in time; it is open but may still be loading.' }
}
